/**
 * VowsEra - Vendor Service
 */

import api from './api';

const vendorService = {
  /**
   * Get all vendors with filters
   */
  getVendors: async (params = {}) => {
    const response = await api.get('/vendors', { params });
    return response.data;
  },

  /**
   * Get vendor by ID
   */
  getVendor: async (id) => {
    const response = await api.get(`/vendors/${id}`);
    return response.data;
  },

  /**
   * Get vendor by slug
   */
  getVendorBySlug: async (slug) => {
    const response = await api.get(`/vendors/slug/${slug}`);
    return response.data;
  },

  /**
   * Get vendors by category
   */
  getVendorsByCategory: async (categorySlug, params = {}) => {
    const response = await api.get(`/vendors/category/${categorySlug}`, { params });
    return response.data;
  },

  /**
   * Get featured vendors
   */
  getFeaturedVendors: async (limit = 8) => {
    const response = await api.get('/vendors/featured', { params: { limit } });
    return response.data;
  },

  /**
   * Search vendors
   */
  searchVendors: async (query, params = {}) => {
    const response = await api.get('/vendors/search', { 
      params: { q: query, ...params } 
    });
    return response.data;
  },

  /**
   * Get vendor categories
   */
  getCategories: async () => {
    const response = await api.get('/vendors/categories');
    return response.data;
  },

  /**
   * Get vendor reviews
   */
  getVendorReviews: async (vendorId, params = {}) => {
    const response = await api.get(`/vendors/${vendorId}/reviews`, { params });
    return response.data;
  },

  /**
   * Submit vendor review
   */
  submitReview: async (vendorId, reviewData) => {
    const response = await api.post(`/vendors/${vendorId}/reviews`, reviewData);
    return response.data;
  },

  /**
   * Check vendor availability
   */
  checkAvailability: async (vendorId, date) => {
    const response = await api.get(`/vendors/${vendorId}/availability`, {
      params: { date }
    });
    return response.data;
  },

  /**
   * Send inquiry to vendor
   */
  sendInquiry: async (vendorId, inquiryData) => {
    const response = await api.post(`/vendors/${vendorId}/inquiries`, inquiryData);
    return response.data;
  },

  /**
   * Get vendor packages
   */
  getVendorPackages: async (vendorId) => {
    const response = await api.get(`/vendors/${vendorId}/packages`);
    return response.data;
  },

  // ============================================
  // Vendor Dashboard (for vendor users)
  // ============================================

  /**
   * Get vendor dashboard stats
   */
  getMyProfile: async () => {
    const response = await api.get("/vendors/dashboard/profile");
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await api.get('/vendors/dashboard/stats');
    return response.data;
  },

  /**
   * Update vendor profile
   */
  updateProfile: async (data) => {
    const response = await api.put('/vendors/profile', data);
    return response.data;
  },

  /**
   * Upload vendor photos
   */
  uploadPhotos: async (formData) => {
    const response = await api.post('/vendors/photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Delete vendor photo
   */
  deletePhoto: async (photoId) => {
    const response = await api.delete(`/vendors/photos/${photoId}`);
    return response.data;
  },

  /**
   * Update vendor packages
   */
  updatePackages: async (packages) => {
    const response = await api.put('/vendors/packages', { packages });
    return response.data;
  },

  /**
   * Create vendor profile (first-time setup)
   */
  createProfile: async (data) => {
    const response = await api.post('/vendors/profile', data);
    return response.data;
  },

  /**
   * Get vendor's own calendar (bookings + blocked dates)
   */
  getCalendar: async (month, year) => {
    const response = await api.get('/availability/my-calendar', { params: { month, year } });
    return response.data;
  },

  /**
   * Update availability for specific dates
   */
  updateCalendarAvailability: async (dates, status, notes) => {
    const response = await api.put('/availability/my-calendar', { dates, status, notes });
    return response.data;
  },

  /**
   * Get vendor bookings
   */
  getBookings: async (params = {}) => {
    const response = await api.get('/vendors/bookings', { params });
    return response.data;
  },

  /**
   * Update booking status
   */
  updateBookingStatus: async (bookingId, status) => {
    const response = await api.put(`/vendors/bookings/${bookingId}/status`, { status });
    return response.data;
  },

  /**
   * Update arbitrary booking fields (package_name, notes, etc.) — used by
   * the vendor portal detail modal to attach a package after the fact.
   */
  updateBooking: async (bookingId, data) => {
    const response = await api.put(`/vendors/bookings/${bookingId}`, data);
    return response.data;
  }
};

export default vendorService;
